import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Typography,
  message,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { useRegisterAdmin } from "hooks/mutations/admin";
import { getAdminLookup } from "hooks/queries/admin";
import { useState } from "react";
import { useAuth } from "context/AuthContextProvider";
import { translate } from "shared/languages";
import { RegisterAdminForm } from "shared/types";
import { Gender, UserType } from "shared/types/enum";
import { hasRole } from "shared/utils/role";
import { DEFAULT_DATE_FORMAT, now } from "shared/utils/dayjs";
import { formatPhone } from "shared/utils/phone";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";

const { Option } = Select;

// Two-step "Tambah Akun" flow for the System Admin, mirroring
// NewInstructorModal and NewCustomerModal:
//  1. Phone only. On continue, classify the number against this business.
//     - already an admin here, staff at another studio, a Spott team account,
//       or a deleted account -> blocked with a message, stay on this step so
//       the number can be corrected.
//     - a client, this studio's instructor, or a deactivated staff member here
//       -> confirm their identity, then grant the tier. The server reuses the
//       stored name, keeps their other roles, and creates or revives the staff
//       row the admin gates require.
//     - nobody -> show the create form.
//
// Before this, ANY existing number was refused outright, so a studio could
// never give its own instructor or a long-standing client a dashboard account.
type Step = "phone" | "create" | "confirm";
type ConfirmKind = "known" | "staff_here" | "staff_inactive_here";

const CONFIRM_COPY: Record<ConfirmKind, string> = {
  known: "messages.info.admin_link_existing",
  staff_here: "messages.info.admin_link_instructor",
  staff_inactive_here: "messages.info.admin_link_inactive_staff",
};

interface RegisterAdminModalProps {
  businessId?: string;
}

const RegisterAdminModal = ({ businessId }: RegisterAdminModalProps) => {
  const [form] = useForm();
  const { state } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState<Step>("phone");
  const [pendingPhone, setPendingPhone] = useState("");
  const [existingName, setExistingName] = useState("");
  const [existingKind, setExistingKind] = useState<ConfirmKind>("known");
  const [existingHasPassword, setExistingHasPassword] = useState(true);
  const [existingIsVerified, setExistingIsVerified] = useState(true);
  const [lookupLoading, setLookupLoading] = useState(false);
  // The toast differs between creating and linking, so the hook is told which.
  const { mutate: registerAdmin, isPending } = useRegisterAdmin(
    step === "confirm",
  );

  const close = () => setIsModalOpen(false);
  const reset = () => {
    form.resetFields();
    setStep("phone");
    setPendingPhone("");
    setExistingName("");
    setExistingKind("known");
    setExistingHasPassword(true);
    setExistingIsVerified(true);
    setLookupLoading(false);
  };

  // Registering an admin is Spott's own action. The server refuses everyone
  // else too; this just keeps the button off the Manager's Akun page.
  if (!hasRole(state.user?.roles, UserType.SysAdmin)) return null;

  // Step 1 -> classify the phone and branch.
  const onContinue = () => {
    form
      .validateFields(["phone_number"])
      .then(async ({ phone_number }) => {
        setLookupLoading(true);
        try {
          const result = await getAdminLookup(businessId ?? "", phone_number);
          const blocked: Partial<Record<string, string>> = {
            admin_here: "messages.error.admin_already_here",
            staff_elsewhere: "messages.error.instructor_staff_elsewhere",
            systemadmin: "messages.error.admin_is_systemadmin",
            deleted: "messages.error.user_deleted",
          };
          const blockedKey = blocked[result.status];
          if (blockedKey) {
            // Stay on this step: the sysadmin most likely mistyped a digit.
            message.error(translate(blockedKey));
            return;
          }
          setPendingPhone(phone_number);
          if (result.status === "new") {
            setStep("create");
            return;
          }
          setExistingName(result.full_name ?? "");
          setExistingKind(result.status as ConfirmKind);
          setExistingHasPassword(!!result.has_password);
          setExistingIsVerified(!!result.is_verified);
          setStep("confirm");
        } catch (e) {
          message.error(translate("messages.error.general"));
        } finally {
          setLookupLoading(false);
        }
      })
      // AntD already paints the inline phone error.
      .catch(() => {});
  };

  const submit = (values: RegisterAdminForm, onDone: () => void) =>
    registerAdmin(values, {
      onSuccess: () => {
        onDone();
        close();
      },
      // Deliberately NOT closing on error: a 409 or 422 used to wipe
      // everything typed, which defeats the point of a two-step flow.
    });

  const onCreate = () => {
    form
      .validateFields()
      .then((values) =>
        submit(
          {
            ...values,
            business_id: businessId ?? "",
            phone_number: pendingPhone,
          },
          reset,
        ),
      )
      .catch(() => {});
  };

  // No name fields: the server reuses the stored identity untouched. A password
  // rides along only when the account has none yet.
  const onConfirmAdd = () => {
    form
      .validateFields(existingHasPassword ? ["role"] : ["role", "password"])
      .then(({ role, password }) =>
        submit(
          {
            business_id: businessId ?? "",
            phone_number: pendingPhone,
            role,
            ...(password ? { password } : {}),
          },
          reset,
        ),
      )
      .catch(() => {});
  };

  const roleField = (
    <Form.Item
      name="role"
      label={translate("label.role")}
      initialValue="admin"
      rules={[{ required: true }]}
    >
      <Select>
        <Option value="admin">{translate("staff_roles.tier_admin")}</Option>
        <Option value="manager">{translate("staff_roles.tier_manager")}</Option>
      </Select>
    </Form.Item>
  );

  const passwordField = (
    <Form.Item
      name="password"
      label={translate("staff_roles.password_label")}
      extra={translate("staff_roles.password_hint")}
      rules={[
        {
          required: true,
          message: `${translate("form.error.input")} ${translate(
            "staff_roles.password_label",
          )} ${translate("form.error.input_end")}`,
        },
        { min: 6, message: translate("form.error.password") },
      ]}
    >
      <Input.Password
        placeholder={`${translate("form.placeholders.enter")} ${translate(
          "staff_roles.password_label",
        )}`}
      />
    </Form.Item>
  );

  const footer = (() => {
    if (step === "phone") {
      return (
        <Flex justify="end" gap={10}>
          <Button onClick={close}>{translate("button.cancel")}</Button>
          <Button type="primary" loading={lookupLoading} onClick={onContinue}>
            {translate("button.continue")}
          </Button>
        </Flex>
      );
    }
    if (step === "create") {
      return (
        <Flex justify="end" gap={10}>
          <Button onClick={() => setStep("phone")}>
            {translate("button.back")}
          </Button>
          <Button type="primary" loading={isPending} onClick={onCreate}>
            {translate("button.add_account")}
          </Button>
        </Flex>
      );
    }
    return (
      <Flex justify="end" gap={10}>
        <Button onClick={() => setStep("phone")}>
          {translate("button.change_number")}
        </Button>
        <Button type="primary" loading={isPending} onClick={onConfirmAdd}>
          {translate("button.confirm_add")}
        </Button>
      </Flex>
    );
  })();

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        type="primary"
        icon={<PlusOutlined />}
      >
        {translate("button.add_account")}
      </Button>
      <Modal
        title={translate("title.create_admin")}
        open={isModalOpen}
        onCancel={close}
        afterClose={reset}
        footer={
          <ConfigProvider theme={modalButtonsTheme}>{footer}</ConfigProvider>
        }
      >
        {businessId ? (
          <Form form={form} layout="vertical">
            {/* Phone is always present; in later steps it's locked context. */}
            <Form.Item
              name="phone_number"
              label={translate("label.phone_number")}
              rules={phoneRules({ smsOtp: true })}
            >
              <PhoneInput
                smsOtp
                disabled={step !== "phone"}
                placeholder={`${translate(
                  "form.placeholders.enter",
                )} ${translate("form.placeholders.phone_number")}`}
              />
            </Form.Item>

            {step === "phone" && (
              <Typography.Paragraph type="secondary">
                {translate("messages.info.admin_phone_lookup")}
              </Typography.Paragraph>
            )}

            {step === "create" && (
              <>
                <Form.Item
                  name="first_name"
                  label={translate("label.first_name")}
                  rules={[
                    {
                      required: true,
                      message: `${translate("form.error.input")} ${translate(
                        "label.first_name",
                      )} ${translate("form.error.input_end")}`,
                    },
                  ]}
                >
                  <Input
                    placeholder={`${translate(
                      "form.placeholders.enter",
                    )} ${translate("label.first_name")}`}
                  />
                </Form.Item>
                <Form.Item
                  name="middle_name"
                  label={translate("label.middle_name")}
                >
                  <Input
                    placeholder={`${translate(
                      "form.placeholders.enter",
                    )} ${translate("label.middle_name")}`}
                  />
                </Form.Item>
                <Form.Item name="last_name" label={translate("label.last_name")}>
                  <Input
                    placeholder={`${translate(
                      "form.placeholders.enter",
                    )} ${translate("label.last_name")}`}
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label={translate("label.email")}
                  rules={[
                    { type: "email", message: translate("form.error.email") },
                  ]}
                >
                  <Input
                    placeholder={`${translate(
                      "form.placeholders.enter",
                    )} ${translate("label.email")}`}
                  />
                </Form.Item>
                {passwordField}
                {roleField}
                <Form.Item name="gender" label={translate("label.gender")}>
                  <Select
                    placeholder={`${translate(
                      "form.placeholders.select",
                    )} ${translate("label.gender")}`}
                  >
                    {Object.values(Gender).map((gender) => (
                      <Option key={gender} value={gender}>
                        {translate(`form.select.gender.${gender}`)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="dob" label={translate("label.dob")}>
                  <DatePicker
                    style={{ width: "100%" }}
                    format={DEFAULT_DATE_FORMAT}
                    disabledDate={(current) => current && current > now()}
                  />
                </Form.Item>
              </>
            )}

            {step === "confirm" && (
              <>
                <Typography.Paragraph>
                  {translate(CONFIRM_COPY[existingKind])}
                </Typography.Paragraph>
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions.Item label={translate("label.name")}>
                    {existingName || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate("label.phone_number")}>
                    {formatPhone(pendingPhone)}
                  </Descriptions.Item>
                </Descriptions>
                {roleField}
                {/* Only when the account has no password yet: mirrors the
                    server's refusal so it is stated here, not as a 422. */}
                {!existingHasPassword && passwordField}
                <ul style={{ paddingInlineStart: 20, marginBottom: 0 }}>
                  <li>
                    <Typography.Text type="secondary">
                      {translate("staff_roles.confirm_admin_rule")}
                    </Typography.Text>
                  </li>
                  {!existingIsVerified && (
                    <li>
                      <Typography.Text type="secondary">
                        {translate("staff_roles.confirm_otp")}
                      </Typography.Text>
                    </li>
                  )}
                  <li>
                    <Typography.Text type="secondary">
                      {translate("messages.info.admin_link_signout")}
                    </Typography.Text>
                  </li>
                </ul>
              </>
            )}
          </Form>
        ) : (
          <Skeleton active />
        )}
      </Modal>
    </>
  );
};

export default RegisterAdminModal;
